<div class="vdi-approval-status" *ngIf="ticketId || ticketNumber">
  <nz-card nzTitle="VDI Approval Status" [nzLoading]="loading" [nzExtra]="extraTemplate">
    <ng-template #extraTemplate>
      <button nz-button nzType="text" nzSize="small" (click)="refresh()" [nzLoading]="loading">
        <i nz-icon nzType="reload"></i>
      </button>
    </ng-template>

    <!-- Loading or checking approval status -->
    <div *ngIf="!hasCheckedApproval" class="loading-approval">
      <nz-skeleton [nzActive]="true" [nzParagraph]="{rows: 3}"></nz-skeleton>
    </div>

    <!-- No approval required -->
    <div *ngIf="hasCheckedApproval && !approval" class="no-approval">
      <nz-result
        nzStatus="info"
        nzTitle="No Approval Required"
        nzSubTitle="This VDI ticket does not require approval.">
      </nz-result>
    </div>

    <!-- Approval status found -->
    <div *ngIf="approval" class="approval-content">
      <!-- Approval Status Section -->
      <div class="status-section">
        <div class="status-header">
          <i nz-icon [nzType]="approvalStatusInfo.icon" [nzTheme]="getIconTheme(approvalStatusInfo.color)"></i>
          <span class="status-text" [ngClass]="'status-' + approvalStatusInfo.color">
            {{ approvalStatusInfo.status }}
          </span>
          <nz-tag *ngIf="approval.approvedBy" [nzColor]="approvalStatusInfo.color">
            {{ approval.approvedBy }}
          </nz-tag>
        </div>
        <p class="status-description">{{ approvalStatusInfo.description }}</p>

        <div *ngIf="approval.approvedDate" class="approval-date">
          <small>{{ approval.approvedDate | date:'medium' }}</small>
        </div>

        <div *ngIf="approval.approvalComments" class="approval-comments">
          <nz-card nzType="inner" nzTitle="Comments">
            <p>{{ approval.approvalComments }}</p>
          </nz-card>
        </div>
      </div>

      <!-- Request Details Section -->
      <div class="details-section">
        <div class="section-header">
          <h4>Request Details</h4>
          <button *ngIf="showRefreshButton" nz-button nzType="link" nzSize="small" 
                  (click)="refreshApprovalData()" [nzLoading]="loading"
                  nz-tooltip nzTooltipTitle="Refresh approval data from ticket details">
            <i nz-icon nzType="sync"></i>
            Refresh Data
          </button>
        </div>
        <nz-alert *ngIf="showRefreshButton" nzType="warning" nzMessage="Data may need refresh" 
                  nzDescription="The requester email appears to be invalid. Click 'Refresh Data' to re-parse ticket details."
                  nzShowIcon [nzCloseable]="false" class="mb-2">
        </nz-alert>
        <nz-descriptions [nzColumn]="1" [nzSize]="'small'">
          <nz-descriptions-item nzTitle="Ticket Number">{{ approval.ticketNumber }}</nz-descriptions-item>
          <nz-descriptions-item nzTitle="Requester">{{ approval.requesterEmail }}</nz-descriptions-item>
          <nz-descriptions-item *ngIf="approval.requesterName" nzTitle="Requester Name">{{ approval.requesterName }}</nz-descriptions-item>
          <nz-descriptions-item *ngIf="approval.serviceDescription" nzTitle="Service Description">{{ approval.serviceDescription }}</nz-descriptions-item>
          <nz-descriptions-item *ngIf="approval.softwareSet" nzTitle="Software Set">{{ approval.softwareSet }}</nz-descriptions-item>
          <nz-descriptions-item nzTitle="GPU Required">
            <nz-tag [nzColor]="approval.gpuRequirement ? 'orange' : 'green'">
              {{ approval.gpuRequirement ? 'Yes' : 'No' }}
            </nz-tag>
          </nz-descriptions-item>
          <nz-descriptions-item nzTitle="Justification">{{ approval.justification }}</nz-descriptions-item>
          <nz-descriptions-item *ngIf="approval.remarks" nzTitle="Additional Remarks">{{ approval.remarks }}</nz-descriptions-item>
        </nz-descriptions>
      </div>

      <!-- VM Creation Status Section -->
      <div class="vm-status-section" *ngIf="approval.vmCreationStatus">
        <h4>VM Creation Status</h4>
        <div class="status-header">
          <i nz-icon [nzType]="vmCreationStatusInfo.icon" [nzTheme]="getIconTheme(vmCreationStatusInfo.color)"></i>
          <span class="status-text" [ngClass]="'status-' + vmCreationStatusInfo.color">
            {{ vmCreationStatusInfo.status }}
          </span>
          <nz-progress *ngIf="vmCreationStatusInfo.progress" [nzPercent]="vmCreationStatusInfo.progress" [nzStatus]="vmCreationStatusInfo.color === 'error' ? 'exception' : 'active'"></nz-progress>
        </div>
        <p class="status-description">{{ vmCreationStatusInfo.description }}</p>

        <!-- VM Details -->
        <div class="vm-details">
          <h5>VM Details</h5>
          <nz-descriptions [nzColumn]="2" [nzSize]="'small'">
            <nz-descriptions-item *ngFor="let detail of vmDetails" [nzTitle]="detail.label">
              <div *ngIf="detail.label === 'Admin Password'" class="password-field">
                <span>{{ detail.value }}</span>
                <button nz-button nzType="text" nzSize="small" (click)="copyToClipboard(approval.adminPassword || '')"
                        [disabled]="!approval.adminPassword" nz-tooltip nzTooltipTitle="Copy password">
                  <i nz-icon nzType="copy"></i>
                </button>
              </div>
              <div *ngIf="detail.label !== 'Admin Password'" class="copyable-field">
                <span>{{ detail.value }}</span>
                <button nz-button nzType="text" nzSize="small" (click)="copyToClipboard(detail.value)"
                        nz-tooltip nzTooltipTitle="Copy to clipboard">
                  <i nz-icon nzType="copy"></i>
                </button>
              </div>
            </nz-descriptions-item>
            <nz-descriptions-item *ngIf="approval.queueMessageId" nzTitle="Queue Message">
              <span>{{ approval.queueMessageId }}</span>
              <button nz-button nzType="text" nzSize="small" (click)="copyToClipboard(approval.queueMessageId)"
                      nz-tooltip nzTooltipTitle="Copy to clipboard">
                <i nz-icon nzType="copy"></i>
              </button>
            </nz-descriptions-item>
            <nz-descriptions-item *ngIf="approval.provisioningAttemptId" nzTitle="Attempt ID">
              <span>{{ approval.provisioningAttemptId }}</span>
              <button nz-button nzType="text" nzSize="small" (click)="copyToClipboard(approval.provisioningAttemptId)"
                      nz-tooltip nzTooltipTitle="Copy to clipboard">
                <i nz-icon nzType="copy"></i>
              </button>
            </nz-descriptions-item>
            <nz-descriptions-item *ngIf="approval.requestedRegion" nzTitle="Region">
              {{ approval.requestedRegion }}
            </nz-descriptions-item>
            <nz-descriptions-item *ngIf="approval.subscriptionId" nzTitle="Subscription">
              {{ approval.subscriptionId }}
            </nz-descriptions-item>
            <nz-descriptions-item *ngIf="approval.resourceGroupName" nzTitle="Resource Group">
              {{ approval.resourceGroupName }}
            </nz-descriptions-item>
          </nz-descriptions>

          <div class="vm-timestamps" *ngIf="approval.vmCreationStartedAt || approval.vmCreationCompletedAt">
            <nz-descriptions [nzColumn]="1" [nzSize]="'small'">
              <nz-descriptions-item *ngIf="approval.vmCreationStartedAt" nzTitle="VM Creation Started">
                {{ approval.vmCreationStartedAt | date:'medium' }}
              </nz-descriptions-item>
              <nz-descriptions-item *ngIf="approval.vmCreationCompletedAt" nzTitle="VM Creation Completed">
                {{ approval.vmCreationCompletedAt | date:'medium' }}
              </nz-descriptions-item>
            </nz-descriptions>
          </div>
          
          <!-- Azure VM Status (Real-time from Azure) -->
          <div *ngIf="azureVmStatus" class="azure-vm-status">
            <nz-divider nzText="Azure VM Status (Live)" nzOrientation="left"></nz-divider>
            <nz-alert 
              nzType="info" 
              nzMessage="Real-time status from Azure"
              nzDescription="This shows the current state of the VM in Azure cloud"
              nzShowIcon
              [nzCloseable]="false"
              class="mb-2">
            </nz-alert>
            
            <nz-descriptions [nzColumn]="2" [nzSize]="'small'" nzBordered>
              <nz-descriptions-item nzTitle="Azure Power State" [nzSpan]="2">
                <i nz-icon [nzType]="azurePowerStateInfo.icon" [nzTheme]="getIconTheme(azurePowerStateInfo.color)"></i>
                <nz-tag [nzColor]="azurePowerStateInfo.color" class="ml-2">
                  {{ azurePowerStateInfo.status }}
                </nz-tag>
                <span class="ml-2">{{ azurePowerStateInfo.description }}</span>
              </nz-descriptions-item>
              
              <nz-descriptions-item nzTitle="Provisioning State">
                <nz-tag [nzColor]="azureVmStatus.isProvisioned ? 'success' : 'error'">
                  {{ azureVmStatus.provisioningState }}
                </nz-tag>
              </nz-descriptions-item>
              
              <nz-descriptions-item nzTitle="Is Running">
                <nz-tag [nzColor]="azureVmStatus.isRunning ? 'success' : 'default'">
                  {{ azureVmStatus.isRunning ? 'Yes' : 'No' }}
                </nz-tag>
              </nz-descriptions-item>
              
              <nz-descriptions-item nzTitle="Last Status Update" [nzSpan]="2">
                <small>{{ azureVmStatus.lastStatusUpdate | date:'medium' }}</small>
                <span class="ml-2 text-muted">(Updates every 30 seconds)</span>
              </nz-descriptions-item>
            </nz-descriptions>
          </div>

          <!-- RDP Access Section -->
          <app-rdp-client
            *ngIf="approval && azureVmStatus"
            [approval]="approval"
            [azureVmStatus]="azureVmStatus"
            [disabled]="loading"
            (connectionStateChanged)="onRdpConnectionChanged($event)">
          </app-rdp-client>
        </div>

        <!-- Failure Information -->
        <div *ngIf="approval.vmCreationStatus === 'Failed'" class="failure-info">
          <nz-alert
            nzType="error"
            nzMessage="VM Creation Failed"
            nzDescription="{{ approval.failureMessage }}"
            nzShowIcon
            nzCloseable
          >
          </nz-alert>
          <div *ngIf="approval.failureCode" class="failure-code">
            <small><strong>Error Code:</strong> {{ approval.failureCode }}</small>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-section" *ngIf="showApprovalActions">
        <nz-divider></nz-divider>
        <h4>Actions</h4>
        <div class="action-buttons">
          <button nz-button nzType="primary" (click)="approveTicket()"
                  [disabled]="loading" nz-tooltip nzTooltipTitle="Approve this VDI request">
            <i nz-icon nzType="check"></i>
            Approve Request
          </button>
          <button nz-button nzType="default" nzDanger (click)="rejectTicket()"
                  [disabled]="loading" nz-tooltip nzTooltipTitle="Reject this VDI request">
            <i nz-icon nzType="close"></i>
            Reject Request
          </button>
        </div>
      </div>
    </div>
  </nz-card>
</div>